/**
 * Shared formatting utilities for checkout components
 */

import moment from "moment-timezone";

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: "£",
  USD: "$",
  EUR: "€",
  AUD: "A$",
  CAD: "C$",
  NZD: "NZ$",
};

/**
 * Format price from minor units (cents/pence) to display string
 * @param price - Price in minor units (e.g., 2900 for £29.00)
 * @param currency - Currency code (e.g., "GBP")
 */
export function formatPrice(price: number, currency = "GBP"): string {
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  return `${symbol}${(price / 100).toFixed(2)}`;
}

/**
 * Get currency symbol
 * @param currency - Currency code (e.g., "GBP")
 */
export function getCurrencySymbol(currency = "GBP"): string {
  return CURRENCY_SYMBOLS[currency] || currency;
}

/**
 * Format date for display
 * @param dateString - ISO date string
 * @param timezone - Optional timezone to display the date in (e.g., "Pacific/Auckland")
 */
export function formatDate(dateString: string, timezone?: string): string {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: timezone,
  };
  return date.toLocaleDateString("en-GB", options);
}

/**
 * Format short date
 */
export function formatShortDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

/**
 * Get a readable timezone label. Returns the full IANA identifier as-is
 * (e.g., "Europe/London", "America/New_York") for clarity.
 */
function getReadableTimezone(timezone?: string): string {
  if (!timezone) return "";
  return timezone;
}

/**
 * The visitor's own IANA timezone, or undefined when we can't tell
 * (server render, or an environment without a usable Intl). Resolved once —
 * the answer can't change within a page session, and this is called from
 * per-session-row render paths.
 */
let viewerTimezoneCache: string | undefined;
let viewerTimezoneResolved = false;

export function resolveViewerTimezone(): string | undefined {
  if (viewerTimezoneResolved) return viewerTimezoneCache;
  viewerTimezoneResolved = true;
  if (typeof window === "undefined") return undefined;
  try {
    viewerTimezoneCache =
      Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || undefined;
  } catch {
    viewerTimezoneCache = undefined;
  }
  return viewerTimezoneCache;
}

/**
 * A zone's UTC offset in minutes east of UTC at a given instant, or null if
 * the zone is unrecognised. moment-timezone is already this package's
 * timezone engine (the appointment flow depends on it) and caches its zone
 * objects, so this is a lookup rather than an Intl.DateTimeFormat
 * construction per call. Not @obby/lib's Timezone__Offset, which silently
 * substitutes the LOCAL offset for an unrecognised zone — the callers here
 * need the null to know the answer is unprovable.
 */
function zoneOffsetMinutes(timeZone: string, date: Date): number | null {
  const zone = moment.tz.zone(timeZone);
  // moment reports minutes west of UTC; negate to the conventional sign.
  return zone ? -zone.utcOffset(date.getTime()) : null;
}

/**
 * Whether an in-person session's timezone is worth naming for this visitor.
 *
 * For someone already on that clock the label is pure noise, and schools have
 * reported customers reading a bare "(Europe/London)" under the listing title
 * as the *venue* and asking where the class actually takes place. It only earns
 * its place when the visitor is browsing from a genuinely different offset,
 * where the wall-clock time would otherwise be ambiguous.
 *
 * Compared by UTC offset at the session's own instant rather than by zone name,
 * so a Dublin parent isn't told a London class is elsewhere, and so the answer
 * is DST-correct for the date being booked rather than for today.
 *
 * When the visitor's zone is unknown (server render) we hide it: local
 * visitors are the overwhelming majority, so that's both the likelier correct
 * answer and the one that avoids a hydration flash of the confusing string.
 */
export function shouldShowTimezoneLabel(
  timezone?: string,
  dateString?: string,
  viewerTimezone: string | undefined = resolveViewerTimezone(),
): boolean {
  if (!timezone || !viewerTimezone) return false;
  if (viewerTimezone === timezone) return false;

  const at = dateString ? new Date(dateString) : new Date();
  const instant = Number.isNaN(at.getTime()) ? new Date() : at;

  const sessionOffset = zoneOffsetMinutes(timezone, instant);
  const viewerOffset = zoneOffsetMinutes(viewerTimezone, instant);
  // An unrecognised zone means we can't prove they match — keep the label.
  if (sessionOffset === null || viewerOffset === null) return true;

  return sessionOffset !== viewerOffset;
}

/**
 * Format time range in the appropriate timezone based on session type:
 * - OFFLINE sessions: Use the course/session timezone
 * - ONLINE sessions: Use the user's local timezone
 */
export function formatTimeRange(
  dateString: string,
  duration?: number,
  timezone?: string,
  isOnline?: boolean,
  viewerTimezone: string | undefined = resolveViewerTimezone(),
): string {
  // Determine which timezone to use for display
  // For offline sessions: use the session/course timezone
  // For online sessions: use the client's local timezone
  const displayTimezone = isOnline ? viewerTimezone || timezone : timezone;

  const options: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: displayTimezone,
  };

  try {
    const date = new Date(dateString);
    const startTime = new Intl.DateTimeFormat("en-GB", options).format(date);

    if (duration) {
      const endDate = new Date(date.getTime() + duration * 60000);
      const endTime = new Intl.DateTimeFormat("en-GB", options).format(endDate);
      // Name the timezone for in-person sessions only, and only when the
      // visitor isn't already on that clock (see shouldShowTimezoneLabel).
      const tzLabel =
        !isOnline && shouldShowTimezoneLabel(timezone, dateString, viewerTimezone)
          ? ` (${getReadableTimezone(displayTimezone)})`
          : "";
      return `${startTime} - ${endTime}${tzLabel}`;
    }

    return startTime;
  } catch {
    // Fallback for environments without Intl support
    const date = new Date(dateString);
    const startTime = date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });

    if (duration) {
      const endDate = new Date(date.getTime() + duration * 60000);
      const endTime = endDate.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      });
      // No timezone suffix here: these fallback times are on the device's own
      // clock (toLocaleTimeString), so naming the course zone would stamp the
      // wrong zone onto them — and this branch fires precisely when the
      // stored zone is one Intl doesn't recognise.
      return `${startTime} - ${endTime}`;
    }

    return startTime;
  }
}

/**
 * Format time only
 */
export function formatTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Format date and time together
 */
export function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
